import { Grid, InputAdornment, InputLabel, useTheme } from '@material-ui/core';

import {
  Backdrop,
  EditIconTwoTone,
  NotificationText,
  PlusIconTwoTone,
  RemoveIconTwoTone,
  Select,
  SelectArrowDownIcon,
  TextInput,
  TrashErrorIconTwoTone,
  useNotificationModal,
} from '@room-match/shared-ui-components';

import React, { useEffect, useMemo, useState } from 'react';
import { IAccountUpdatePayload } from 'shared/interfaces/IAccount';
import { accountService } from 'shared/services/accountService';
import {
  AddNewLocationButton,
  Column,
  CustomIconButton,
  FlexContainer,
  Heading,
  LocationActions,
  LocationContainer,
  MainCont,
  OneColumnGrid,
  SaveButton,
  TwoColumn6Grid,
  TwoColumnGrid,
  useStyles,
} from './GeneralInformation.styles';

import { useQueryClient } from 'react-query';
import { mappedCountry } from 'shared/utils/countryStateMapper';
import { getErrorMessage } from 'shared/utils/room-shared';
import { useGeneralInformationForm } from './GeneralInformationForm';

import { LocationModal } from 'components/LocationModal';
import unionList from 'data/Unions.json';
import { getIn } from 'formik';
import { IUnion } from 'shared/interfaces/IAccount';
import { IAddress } from 'shared/interfaces/IAddress';
import { countryStateApiService } from 'shared/services/countryStateApiService';
import { locationService } from 'shared/services/locationService';
import { talentService } from 'shared/services/talentService';
// import LocationModal from 'components/LocationModal/NewLocationModal';
import DatePicker from 'components/RegistrationForm/MinorForm/AddNewMember/DatePicker/DatePicker';
import moment from 'moment';
import { useAppSelector } from 'shared/redux/store';
import { ls } from 'shared/utils/ls';

const { getCountries } = countryStateApiService();
const { getAccount, updateAccount } = accountService();
const { deleteLocation } = locationService();
const { updateTalent } = talentService();

const id = 'Real ID Expiration Date';

const GeneralInformation = () => {
  const theme = useTheme().palette.type;
  const classes = useStyles();
  const { setLS } = ls();
  const { app: appState } = useAppSelector((state) => state);
  const { guardianUI } = appState.guardian;
  const [getAccountIsLoading, setgetAccountIsLoading] = useState(true);
  const { data: accountDetails, isLoading } = getAccount();
  const country_code = accountDetails?.data?.attributes.other_phone_numbers?.find(
    (ph) => ph?.type === 'primary',
  )?.country_code;
  const { mutateAsync, isLoading: updateAccountIsLoading } = updateAccount();
  const { mutateAsync: updateTalentWorkingPermit } = updateTalent();
  const { mutateAsync: deleteLocationMutateAsync, isLoading: deleteLocationIsLoading } = deleteLocation();
  const [selectedLocation, setSelectedLocation] = useState<IAddress | null>(null);
  const [openLocationModal, handleOpenLocationModal] = useState<boolean>(false);
  const [showSuccess, handleShowSuccess] = useState<boolean>(false);
  const { handleOpen, isOpen, NotificationModal, handleSetMessage, handleChangeModalType } = useNotificationModal({
    type: 'error',
  });

  const { data: countriesData } = getCountries();

  const [unionToAdd, setUnionToAdd] = useState<IUnion>({
    name: '',
    id_number: '',
  });

  const queryClient = useQueryClient();

  const getAccountDetails = useMemo(() => {
    return accountDetails ? accountDetails.data : undefined;
  }, [accountDetails]);

  const handleSubmit = async (values: IAccountUpdatePayload) => {
    const newValue = { ...values };
    //removed to fixed the issue of stage name already taken, since, the error has already been removed on BE
    // delete newValue.stage_first_name;
    // delete newValue.stage_middle_name;
    // delete newValue.stage_last_name;

    mutateAsync(newValue, {
      onSuccess: (res) => {
        if (res.data.attributes.user_types.includes('guardian')) {
          setLS('guardian_account', JSON.stringify(res.data));
        }
        queryClient.invalidateQueries(['accounts'], {
          refetchInactive: true,
        });
        handleShowSuccess(true);
      },
      onError: (errors: any) => {
        if (errors?.response?.data?.errors?.base?.[0]?.includes('Stage name has already been taken')) {
          handleSetMessage(
            'Stage Name Update Error',
            `You cannot use this stage name as it has already been taken.`,
            'OK',
          );
          handleOpen();
        }
      },
    });
    updateTalentWorkingPermit({
      with_minor_work_permit: values.with_minor_work_permit,
      work_permit_expired_at: values.work_permit_expired_at as any,
    });
  };

  const { form } = useGeneralInformationForm({
    onSubmit: handleSubmit,
    data: getAccountDetails,
  });

  const [workPermit, setworkPermit] = useState<boolean>(form.values.with_minor_work_permit || false);

  useEffect(() => {
    if (getAccountDetails?.attributes?.with_minor_work_permit) {
      setworkPermit(getAccountDetails?.attributes?.with_minor_work_permit);
    }
  }, [getAccountDetails?.attributes?.with_minor_work_permit]);

  const getCountry = (country_code: String) => {
    return mappedCountry(countriesData || []).find((v) => v.value === country_code)?.key || '';
  };

  const handleLocationDelete = async (locationId: string) => {
    deleteLocationMutateAsync(
      { locationId: locationId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['accounts'], {
            refetchInactive: true,
          });
        },
        onError: (error) => {
          if (error) {
            handleSetMessage('Location Delete Error', `You need to atleast have 1 primary city address.`, 'OK');
            handleOpen();
          }
        },
      },
    );
  };

  const addUnion = () => {
    const getUnionValues = form.values.unions || [];
    form.setFieldValue('unions', [
      ...getUnionValues,
      {
        name: unionToAdd.name,
        id_number: unionToAdd.id_number,
      },
    ]);

    setUnionToAdd({ name: '', id_number: '' });
  };

  const removeUnion = (index: number) => {
    const getUnionValues = form.values.unions || [];
    const filteredUnions = getUnionValues.filter((_, i) => i !== index);
    form.setFieldValue('unions', filteredUnions);
  };

  useEffect(() => {
    if (!isLoading) {
      setTimeout(() => {
        setgetAccountIsLoading(false);
      }, 1000);
    }
  }, [isLoading]);

  if (getAccountIsLoading || updateAccountIsLoading || deleteLocationIsLoading)
    return (
      <Backdrop
        isLoading={
          getAccountIsLoading
            ? getAccountIsLoading
            : updateAccountIsLoading
              ? updateAccountIsLoading
              : deleteLocationIsLoading
                ? deleteLocationIsLoading
                : false
        }
      />
    );

  const primaryAddresses =
    accountDetails?.data.attributes.addresses &&
    accountDetails?.data.attributes.addresses.data.filter(
      (v) => v.attributes.primary === true && v.attributes.type === 'work',
    );

  return (
    <>
      <MainCont>
        <Grid item>
          <p style={{ fontSize: 25 }}>General Information</p>
        </Grid>
        <Grid item>
          <Heading>Legal Name</Heading>
          <Column>
            <Grid item lg={3} md={3} xs={12}>
              <TextInput
                label={'First Name *'}
                placeholder="Enter first name…"
                name="first_name"
                value={form.values.first_name}
                onChange={(e) => {
                  if (form.errors.first_name && !form.touched.first_name) {
                    form.setFieldTouched('first_name');
                    form.validateField('first_name');
                  }
                  form.handleChange(e);
                }}
                onBlur={form.handleBlur}
                InputProps={{
                  classes: {
                    root: classes.textInputStyle,
                  },
                }}
                errorMessage={getErrorMessage(form.touched.first_name, form.errors.first_name)}
              />
            </Grid>
            <Grid item lg={3} md={3} xs={12}>
              <TextInput
                label={'Middle Name'}
                placeholder="Enter middle name…"
                name="middle_name"
                value={form.values.middle_name}
                InputProps={{
                  classes: {
                    root: classes.textInputStyle,
                  },
                }}
                onChange={(e) => {
                  if (form.errors.middle_name && !form.touched.middle_name) {
                    form.setFieldTouched('middle_name');
                    form.validateField('middle_name');
                  }
                  form.handleChange(e);
                }}
                onBlur={form.handleBlur}
                errorMessage={getErrorMessage(form.touched.middle_name, form.errors.middle_name)}
              />
            </Grid>
            <Grid item lg={3} md={3} xs={12}>
              <TextInput
                label={'Last Name *'}
                placeholder="Enter last name…"
                name="last_name"
                value={form.values.last_name}
                InputProps={{
                  classes: {
                    root: classes.textInputStyle,
                  },
                }}
                onChange={(e) => {
                  if (form.errors.last_name && !form.touched.last_name) {
                    form.setFieldTouched('last_name');
                    form.validateField('last_name');
                  }
                  form.handleChange(e);
                }}
                onBlur={form.handleBlur}
                errorMessage={getErrorMessage(form.touched.last_name, form.errors.last_name)}
              />
            </Grid>
          </Column>
        </Grid>
        {guardianUI ? (
          <React.Fragment />
        ) : (
          <Grid item>
            <Heading>Stage Name</Heading>
            <Column>
              <Grid item lg={3} md={3} xs={12}>
                <TextInput
                  label={'First Name *'}
                  placeholder="Enter first name…"
                  name="stage_first_name"
                  value={form.values.stage_first_name}
                  InputProps={{
                    classes: {
                      root: classes.textInputStyle,
                    },
                  }}
                  onChange={(e) => {
                    if (form.errors.stage_first_name && !form.touched.stage_first_name) {
                      form.setFieldTouched('stage_first_name');
                      form.validateField('stage_first_name');
                    }
                    form.handleChange(e);
                  }}
                  onBlur={form.handleBlur}
                  errorMessage={getErrorMessage(form.touched.stage_first_name, form.errors.stage_first_name)}
                />
              </Grid>
              <Grid item lg={3} md={3} xs={12}>
                <TextInput
                  label={'Middle Name'}
                  placeholder="Enter middle name…"
                  name="stage_middle_name"
                  value={form.values.stage_middle_name}
                  InputProps={{
                    classes: {
                      root: classes.textInputStyle,
                    },
                  }}
                  onChange={(e) => {
                    if (form.errors.stage_middle_name && !form.touched.stage_middle_name) {
                      form.setFieldTouched('stage_middle_name');
                      form.validateField('stage_middle_name');
                    }
                    form.handleChange(e);
                  }}
                  onBlur={form.handleBlur}
                  errorMessage={getErrorMessage(form.touched.stage_middle_name, form.errors.stage_middle_name)}
                />
              </Grid>
              <Grid item lg={3} md={3} xs={12}>
                <TextInput
                  label={'Last Name'}
                  placeholder="Enter last name…"
                  name="stage_last_name"
                  value={form.values.stage_last_name}
                  InputProps={{
                    classes: {
                      root: classes.textInputStyle,
                    },
                  }}
                  onChange={(e) => {
                    if (form.errors.stage_last_name && !form.touched.stage_last_name) {
                      form.setFieldTouched('stage_last_name');
                      form.validateField('stage_last_name');
                    }
                    form.handleChange(e);
                  }}
                  onBlur={form.handleBlur}
                  errorMessage={getErrorMessage(form.touched.stage_last_name, form.errors.stage_last_name)}
                />
              </Grid>
            </Column>
          </Grid>
        )}
        {/* <Grid item>
          <Heading>Phone Number/s</Heading>
          <Column>
            {form.values.other_phone_numbers &&
              Array.isArray(form.values.other_phone_numbers) &&
              form.values.other_phone_numbers.map((phoneData: IPhoneNumber, index) => (
                <Grid item lg={3} md={6} xs={12}>
                  <InputLabel id="contactNumber" style={{ marginBottom: '-15px' }}>
                    {capitalizeFirstLetter(phoneData.type)} {phoneData.type === 'primary' ? '*' : '(Optional)'}
                  </InputLabel>
                  <SingleFieldContactInput
                    country={
                      phoneData.country_code.toLowerCase() || getAccountDetails?.attributes.country_code.toLowerCase()
                    }
                    inputValue={phoneData.number}
                    onChange={(value, data) => {
                      if (phoneData.type === 'primary' && form.values.other_phone_numbers) {
                        form.values.other_phone_numbers.forEach((phone, i) => {
                          if (phone.number === '' && phone.type === 'primary') {
                            form.setFieldValue(`other_phone_numbers[${i}].country_code`, data.countryCode);
                          }
                        });
                      }

                      form.setFieldValue(`other_phone_numbers[${index}].country_code`, data.countryCode);
                      form.setFieldValue(`other_phone_numbers[${index}].number`, value);
                    }}
                  />
                </Grid>
              ))}
          </Column>
        </Grid> */}
        <Grid item>
          <Heading>Union Status & IDs</Heading>
          <OneColumnGrid item lg={12} md={12} xs={12} $gutter={55}>
            {form.values.unions &&
              Array.isArray(form.values.unions) &&
              form.values.unions.map((union, index) => (
                <TwoColumn6Grid item lg={6} md={12} xs={12}>
                  <Grid item lg={12} md={12} xs={12}>
                    <Select
                      variant="outlined"
                      data={unionList}
                      // IconComponent={SelectArrowDownIcon}
                      fullWidth
                      placeHolder="Select a Union"
                      value={union.name}
                      className={classes.select}
                      onChange={(e) => {
                        const { value } = e.target as { value: string };
                        form.setFieldValue(`unions[${index}].name`, value);
                      }}
                      errorMessage={getErrorMessage(
                        getIn(form.touched, `unions[${index}].name`),
                        getIn(form.errors, `unions[${index}].name`),
                      )}
                    />
                  </Grid>

                  <Grid item lg={12} md={12} xs={12}>
                    <TextInput
                      placeholder="Enter ID"
                      name={`unions[${index}].id_number`}
                      value={union.id_number}
                      onChange={(e) => {
                        const { value } = e.target as { value: string };
                        form.setFieldValue(`unions[${index}].id_number`, value);
                      }}
                      errorMessage={getErrorMessage(
                        getIn(form.touched, `unions[${index}].id_number`),
                        getIn(form.errors, `unions[${index}].id_number`),
                      )}
                      InputProps={{
                        classes: {
                          root: classes.textInputStyle,
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <RemoveIconTwoTone
                              onClick={() => removeUnion(index)}
                              style={{ fontSize: '16px', cursor: 'pointer' }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </TwoColumn6Grid>
              ))}
          </OneColumnGrid>
          <OneColumnGrid item lg={12} md={12} xs={12} $gutter={25}>
            <Grid item lg={6} md={12} xs={12}>
              <AddNewLocationButton fullWidth startIcon={<PlusIconTwoTone />} onClick={() => addUnion()}>
                Add New Union
              </AddNewLocationButton>
            </Grid>
          </OneColumnGrid>
        </Grid>
        {getAccountDetails?.attributes?.adult_minor === 'minor' && (
          <Grid item>
            <Heading>Working Permit</Heading>
            <OneColumnGrid item lg={12} md={12} xs={12} $gutter={25}>
              <Grid item lg={6} md={12} xs={12}>
                <InputLabel>Does the minor have a current work permit?</InputLabel>
                <Select
                  variant="outlined"
                  fullWidth
                  data={[
                    { key: 'Yes', value: 'Yes' },
                    { key: 'No', value: 'No' },
                  ]}
                  value={workPermit ? 'Yes' : 'No'}
                  IconComponent={SelectArrowDownIcon}
                  onChange={(e) => {
                    setworkPermit(e.target.value === 'Yes');
                    form.setFieldValue('with_minor_work_permit', e.target.value === 'Yes');
                  }}
                />
                {workPermit && (
                  <Grid item>
                    <InputLabel style={{ marginBottom: 10 }}>Work permit expiration date</InputLabel>
                    <DatePicker
                      theme={theme}
                      componentType="input"
                      onChooseDate={(value) => {
                        form.setFieldValue(
                          'work_permit_expired_at',
                          moment(value as any)
                            .utc(true)
                            .format('YYYY-MM-DD'),
                        );
                      }}
                      value={
                        (form.values.work_permit_expired_at ||
                          getAccountDetails?.attributes?.work_permit_expired_at ||
                          undefined) as any
                      }
                    />
                  </Grid>
                )}
              </Grid>
            </OneColumnGrid>
          </Grid>
        )}

        <Grid item>
          <Heading>Working Location/s</Heading>
          <OneColumnGrid item lg={12} md={12} xs={12} $gutter={25}>
            <Grid item lg={6} md={12} xs={12}>
              <InputLabel id="primaryLocation">Primary City</InputLabel>
              {primaryAddresses && primaryAddresses.length > 0 ? (
                primaryAddresses.map((i) => (
                  <LocationContainer>
                    <p style={{ fontSize: 14 }}>
                      {i.attributes.city_suburb} / {getCountry(i.attributes.country_code) || i.attributes.country_code}{' '}
                      {i.attributes.zip_code ? ' / ' + i.attributes.zip_code : ''}
                    </p>
                    <LocationActions>
                      <CustomIconButton>
                        <EditIconTwoTone
                          onClick={() => {
                            setSelectedLocation(i);
                            handleOpenLocationModal(true);
                          }}
                        />
                      </CustomIconButton>
                      {primaryAddresses.length > 1 && (
                        <CustomIconButton>
                          <TrashErrorIconTwoTone onClick={() => handleLocationDelete(i.id)} />
                        </CustomIconButton>
                      )}
                    </LocationActions>
                  </LocationContainer>
                ))
              ) : (
                <LocationContainer $isEmpty={true}>
                  <p style={{ fontSize: 14 }}>No Location Added</p>
                  <LocationActions></LocationActions>
                </LocationContainer>
              )}
            </Grid>
          </OneColumnGrid>
          <Grid item lg={12} md={12} xs={12} style={{ marginTop: 20 }}>
            <InputLabel id="primaryLocation">Other Cities</InputLabel>
            <TwoColumnGrid item>
              {accountDetails?.data.attributes.addresses.data
                .filter((v) => v.attributes.primary === false && v.attributes.type === 'work')
                .map((i) => (
                  <Grid item lg={12} md={12} xs={12}>
                    <LocationContainer>
                      <p style={{ fontSize: 14 }}>
                        {i.attributes.city_suburb} /{' '}
                        {getCountry(i.attributes.country_code) || i.attributes.country_code}{' '}
                        {i.attributes.zip_code ? ' / ' + i.attributes.zip_code : ''}
                      </p>
                      <LocationActions>
                        <CustomIconButton>
                          <EditIconTwoTone
                            onClick={() => {
                              setSelectedLocation(i);
                              handleOpenLocationModal(true);
                            }}
                          />
                        </CustomIconButton>
                        <CustomIconButton>
                          <TrashErrorIconTwoTone onClick={() => handleLocationDelete(i.id)} />
                        </CustomIconButton>
                      </LocationActions>
                    </LocationContainer>
                  </Grid>
                ))}
              <Grid item lg={12} md={12} xs={12}>
                <AddNewLocationButton
                  fullWidth
                  startIcon={<PlusIconTwoTone />}
                  onClick={() => {
                    setSelectedLocation(null);
                    handleOpenLocationModal(true);
                  }}
                >
                  Add New Location
                </AddNewLocationButton>
              </Grid>
            </TwoColumnGrid>
          </Grid>
        </Grid>
        <Grid item>
          <Heading>Other/s</Heading>
          <Column>
            <Grid item lg={6} md={6} xs={12}>
              <InputLabel id="email">Passport Expiration Date</InputLabel>
              <DatePicker
                theme={theme}
                fullWidth
                label="Passport Expiration Date"
                componentType="input"
                onChooseDate={(value) => {
                  form.setFieldValue(
                    'passport_expiration_date',
                    moment(value as any)
                      .utc(true)
                      .format('YYYY-MM-DD'),
                  );
                }}
                value={
                  (form.values.passport_expiration_date ||
                    getAccountDetails?.attributes?.passport_expiration_date ||
                    undefined) as any
                }
              />
            </Grid>
            <Grid item lg={6} md={6} xs={12}>
              <InputLabel id="driversLicense">Driver's License Expiration Date</InputLabel>
              <DatePicker
                theme={theme}
                fullWidth
                label="Driver's License Expiration Date"
                componentType="input"
                onChooseDate={(value) => {
                  form.setFieldValue(
                    'drivers_license_expiration_date',
                    moment(value as any)
                      .utc(true)
                      .format('YYYY-MM-DD'),
                  );
                }}
                value={
                  (form.values.drivers_license_expiration_date ||
                    getAccountDetails?.attributes?.drivers_license_expiration_date ||
                    undefined) as any
                }
              />
            </Grid>
            {country_code === 'us' ? (
              <Grid item lg={6} md={6} xs={12}>
                <InputLabel id={id}>Real ID Expiration Date</InputLabel>
                <DatePicker
                  theme={theme}
                  fullWidth
                  label={id}
                  componentType="input"
                  onChooseDate={(value) => {
                    form.setFieldValue(
                      'real_id_expiration_date',
                      moment(value as any)
                        .utc(true)
                        .format('YYYY-MM-DD'),
                    );
                  }}
                  value={
                    (form.values.real_id_expiration_date ||
                      getAccountDetails?.attributes?.real_id_expiration_date ||
                      undefined) as any
                  }
                />
              </Grid>
            ) : null}
          </Column>
        </Grid>
        <Grid item>
          <FlexContainer item>
            <SaveButton
              color="primary"
              variant="contained"
              disableElevation
              onClick={() => form.handleSubmit()}
              disabled={!form.dirty}
            >
              Save Changes
            </SaveButton>
            <NotificationText show={showSuccess} setShow={handleShowSuccess} />
          </FlexContainer>
        </Grid>
      </MainCont>
      <LocationModal
        open={openLocationModal}
        onClose={handleOpenLocationModal}
        data={selectedLocation}
        ifNoAddresses={
          accountDetails &&
          accountDetails?.data.attributes.addresses.data.filter(
            (v) => v.attributes.primary === true && v.attributes.type === 'work',
          ).length > 0
        }
        type="work"
      />

      {isOpen && <NotificationModal />}
    </>
  );
};

export default GeneralInformation;
